import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicBlogService } from "../services/publicApi";
import { getBlogCoverSrc, getBlogFallbackSrc } from "../utils/blogImage";
import {
  ensureMetaTag,
  ensureCanonicalLink,
  setPageJsonLd,
  buildBreadcrumbJsonLd
} from "../utils/seoDom";

const formatBlogDate = (value) => {
  if (!value) return "Publication recente";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Publication recente";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const normalizeContent = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "<p>Le contenu de cet article sera bientot disponible.</p>";
  }

  if (/<[a-z][\s\S]*>/i.test(raw)) {
    return raw;
  }

  return raw
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br />")}</p>`)
    .join("");
};

const SITE_URL = "https://www.domoelec.ma";

const truncate = (s, n) => {
  const str = String(s || "");
  if (str.length <= n) return str;
  return str.slice(0, n - 1).trim() + "…";
};
const plainText = (raw) =>
  String(raw || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);
        setError("");
        const [blogItem, recentItems] = await Promise.all([
          publicBlogService.getBlogBySlug(slug),
          publicBlogService.getPublicBlogs(6),
        ]);
        setBlog(blogItem || null);
        setRecentBlogs(
          (Array.isArray(recentItems) ? recentItems : []).filter(
            (item) => item.slug !== slug
          )
        );
      } catch (_) {
        setBlog(null);
        setRecentBlogs([]);
        setError("Article introuvable ou indisponible.");
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [slug]);

  useEffect(() => {
    if (!blog) return;
    const canonicalUrl = `${SITE_URL}/blog/${encodeURIComponent(blog.slug || slug)}`;
    const excerpt = plainText(blog.excerpt || blog.content || "");
    const rawTitle = `${blog.title} | Blog DOMOELEC`;
    const title = truncate(rawTitle, 60);
    const description = truncate(excerpt || `Article DOMOELEC sur le courant faible, la sécurité électronique et la domotique au Maroc.`, 160);
    document.title = title;
    ensureMetaTag("name", "description").setAttribute("content", description);
    ensureMetaTag("property", "og:title").setAttribute("content", title);
    ensureMetaTag("property", "og:description").setAttribute("content", description);
    ensureMetaTag("property", "og:type").setAttribute("content", "article");
    ensureMetaTag("property", "og:url").setAttribute("content", canonicalUrl);
    ensureMetaTag("name", "twitter:title").setAttribute("content", title);
    ensureMetaTag("name", "twitter:description").setAttribute("content", description);
    if (blog.coverImageUrl) {
      ensureMetaTag("property", "og:image").setAttribute("content", blog.coverImageUrl);
      ensureMetaTag("name", "twitter:image").setAttribute("content", blog.coverImageUrl);
    }
    if (blog.publishedAt) {
      ensureMetaTag("property", "article:published_time").setAttribute("content", new Date(blog.publishedAt).toISOString());
    }
    ensureCanonicalLink().setAttribute("href", canonicalUrl);

    // Données structurées JSON-LD (Organization + Article + Breadcrumb),
    // les blocs précédents sont remplacés.
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: blog.title,
      description: description,
      image: blog.coverImageUrl ? [blog.coverImageUrl] : undefined,
      author: {
        '@type': 'Organization',
        name: 'DOMOELEC',
        url: SITE_URL
      },
      publisher: {
        '@type': 'Organization',
        name: 'DOMOELEC',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/images/logo-domoelec.png`
        }
      },
      datePublished: blog.publishedAt ? new Date(blog.publishedAt).toISOString() : undefined,
      dateModified: blog.updatedAt ? new Date(blog.updatedAt).toISOString() : undefined,
      url: canonicalUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl
      }
    };

    const breadcrumbSchema = buildBreadcrumbJsonLd([
      { name: 'Accueil', item: `${SITE_URL}/` },
      { name: 'Blog', item: `${SITE_URL}/blog` },
      { name: blog.title, item: canonicalUrl }
    ]);

    setPageJsonLd([articleSchema, breadcrumbSchema]);
  }, [blog, slug]);

  const articleHtml = useMemo(() => normalizeContent(blog?.content), [blog]);
  const articleDate = formatBlogDate(blog?.publishedAt || blog?.createdAt || blog?.updatedAt);

  return (
    <div className="blog-detail-page">
      <section className="blog-detail-section">
        <div className="container">
          {loading ? (
            <div className="blog-empty-state">Chargement de l'article...</div>
          ) : error || !blog ? (
            <div className="blog-empty-state">
              <p>{error || "Article introuvable."}</p>
              <Link to="/blog" className="blog-primary-link">
                Retour au blog
              </Link>
            </div>
          ) : (
            <div className="blog-detail-layout">
              <article className="blog-detail-article">
                <div className="blog-detail-head">
                  <div className="blog-breadcrumb">
                    <Link to="/">Accueil</Link>
                    <span>/</span>
                    <Link to="/blog">Blog</Link>
                  </div>
                  <div className="blog-card__meta-line blog-card__meta-line--detail">
                    <span>Article</span>
                    <span>/</span>
                    <span>{articleDate}</span>
                  </div>
                  <h1>{blog.title}</h1>
                  {blog.excerpt && <p className="blog-detail-excerpt">{blog.excerpt}</p>}
                  <div className="blog-detail-head__meta">
                    <span className="blog-detail-author">
                      <i className="fas fa-user-circle"></i>
                      <span>DOMOELEC</span>
                    </span>
                    <div className="blog-tag-row">
                      {(Array.isArray(blog.tags) ? blog.tags : []).slice(0, 3).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="blog-detail-cover blog-detail-cover--hero">
                  <img
                    src={getBlogCoverSrc(blog)}
                    alt={blog.title}
                    onError={(e) => {
                      if (e.currentTarget.dataset.fallbackApplied === "1") return;
                      e.currentTarget.dataset.fallbackApplied = "1";
                      e.currentTarget.src = getBlogFallbackSrc(blog);
                    }}
                  />
                </div>

                <div className="blog-detail-body">
                  <div
                    className="blog-detail-content"
                    dangerouslySetInnerHTML={{ __html: articleHtml }}
                  />

                  <div className="blog-detail-cta">
                    <div className="blog-detail-cta__copy">
                      <span className="blog-meta-chip">Parlons de votre projet</span>
                      <h3>Besoin d'un accompagnement technique sur mesure ?</h3>
                      <p>
                        DOMOELEC vous accompagne pour choisir la bonne solution,
                        structurer votre projet et obtenir un devis adapte.
                      </p>
                    </div>
                    <div className="blog-detail-cta__actions">
                      <Link to="/contact" className="blog-primary-link">
                        Obtenir un devis
                      </Link>
                      {blog.videoUrl && (
                        <a
                          href={blog.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="blog-detail-link"
                        >
                          Voir la video
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>

              <aside className="blog-detail-sidebar">
                <div className="blog-sidebar-card">
                  <h3>Articles recents</h3>
                  <div className="blog-sidebar-list">
                    {recentBlogs.slice(0, 4).map((item) => (
                      <Link
                        key={item.id || item.slug}
                        to={`/blog/${item.slug}`}
                        className="blog-sidebar-item"
                      >
                        <img
                          src={getBlogCoverSrc(item)}
                          alt={item.title}
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.dataset.fallbackApplied === "1") return;
                            e.currentTarget.dataset.fallbackApplied = "1";
                            e.currentTarget.src = getBlogFallbackSrc(item);
                          }}
                        />
                        <div>
                          <span className="blog-meta-date">
                            {formatBlogDate(item.publishedAt || item.createdAt || item.updatedAt)}
                          </span>
                          <strong>{item.title}</strong>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
